/**
 * Cofre local: cifra o estado do aparelho com uma frase.
 *
 * Existe porque docs/04 conta A4 — "quem tem o aparelho na mao" — como
 * adversario real. No celular a resposta e SecureStore mais SQLCipher; num
 * navegador nao ha equivalente, e `localStorage` e texto claro para qualquer
 * um que abra o console ou copie o perfil. Entao o que vai para o disco ja
 * vai cifrado, e a chave nunca fica guardada: e derivada da frase, toda vez.
 *
 * O QUE ISTO NAO RESOLVE, e precisa aparecer na UI:
 *
 *   - Frase fraca continua sendo frase fraca. O Argon2id encarece cada
 *     tentativa, nao torna "123456" segura.
 *   - Enquanto o app esta aberto, a identidade esta decifrada em memoria.
 *     Contra malware que le a memoria (A5), isto nao faz nada.
 *   - Nao ha PIN de coacao aqui. Isso e da Fase 5.
 *
 * Formato do blob:
 *
 *   0   1   versao = 1
 *   1  16   salt do Argon2id
 *  17   n   seal() — nonce (24) ‖ ciphertext ‖ tag (16)
 *
 * O salt vai em claro, como deve: ele nao e segredo, existe para que dois
 * cofres com a mesma frase nao produzam a mesma chave.
 *
 * A frase entra como BYTES, nao como string. Duas razoes: `TextEncoder` e API
 * de plataforma e este pacote nao depende de nenhuma; e string em JS e imutavel,
 * entao quem passa a frase nao teria como apaga-la da memoria depois — com
 * `Uint8Array`, tem.
 */
import { open, seal } from './aead.js'
import { concat } from './bytes.js'
import { DecryptionFailedError } from './errors.js'
import { PWHASH, SIZES, sodium } from './sodium.js'

export const VAULT_VERSION = 1
const HEADER_BYTES = 1 + PWHASH.saltBytes

/** O cofre e cifrado com esta chave; ela nunca e persistida. */
function deriveKey(passphrase: Uint8Array, salt: Uint8Array): Uint8Array {
  return sodium().crypto_pwhash(
    SIZES.symmetricKey,
    passphrase,
    salt,
    PWHASH.opsLimit,
    PWHASH.memLimit,
    PWHASH.algorithm,
  )
}

export function sealVault(passphrase: Uint8Array, plaintext: Uint8Array): Uint8Array {
  if (passphrase.length === 0) throw new Error('frase vazia')
  const s = sodium()
  const salt = s.randombytes_buf(PWHASH.saltBytes)
  const key = deriveKey(passphrase, salt)
  try {
    return concat(Uint8Array.of(VAULT_VERSION), salt, seal(key, plaintext))
  } finally {
    // A chave sai da memoria assim que o cofre esta selado. Nao adianta
    // contra quem le a memoria no instante certo, mas encurta a janela.
    s.memzero(key)
  }
}

/**
 * Abre o cofre. Frase errada e blob adulterado dao o MESMO erro, de
 * proposito: distinguir os dois diria a quem tenta forca bruta que a frase
 * estava certa e o problema era outro.
 */
export function openVault(passphrase: Uint8Array, blob: Uint8Array): Uint8Array {
  if (blob.length <= HEADER_BYTES + SIZES.aeadNonce + SIZES.aeadTag) {
    throw new DecryptionFailedError()
  }
  if (blob[0] !== VAULT_VERSION) throw new DecryptionFailedError()

  const s = sodium()
  const key = deriveKey(passphrase, blob.slice(1, HEADER_BYTES))
  try {
    const out = open(key, blob.slice(HEADER_BYTES))
    if (out === undefined) throw new DecryptionFailedError()
    return out
  } finally {
    s.memzero(key)
  }
}
